import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Target, 
  Plus, 
  Calendar, 
  Clock, 
  BookOpen, 
  TrendingUp,
  CheckCircle,
  Edit3,
  Trash2,
  Flag,
  Award,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { getStudentProfileByToken } from '../../services/elearningStudentService';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import StudentLayout from '../../components/student/StudentLayout';

interface Goal {
  id: string;
  title: string;
  description: string;
  type: 'daily' | 'weekly' | 'monthly' | 'course_completion' | 'skill_mastery';
  targetValue: number;
  currentValue: number;
  unit: 'minutes' | 'lessons' | 'modules' | 'quizzes' | 'courses';
  targetDate?: string;
  isCompleted: boolean;
  createdAt: string;
  priority: 'low' | 'medium' | 'high';
}

const StudentGoalsPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  
  // État pour le nouveau goal
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    type: 'weekly' as Goal['type'],
    targetValue: 1,
    unit: 'lessons' as Goal['unit'],
    targetDate: '',
    priority: 'medium' as Goal['priority']
  });

  // Données des objectifs simulées
  const [goals, setGoals] = useState<Goal[]>([
    {
      id: '1',
      title: 'Étudier quotidiennement',
      description: 'Consacrer au moins 30 minutes par jour à l\'apprentissage',
      type: 'daily',
      targetValue: 30,
      currentValue: 25,
      unit: 'minutes',
      isCompleted: false,
      createdAt: '2024-01-01',
      priority: 'high'
    },
    {
      id: '2',
      title: 'Terminer 3 cours ce mois',
      description: 'Compléter 3 cours complets avant la fin du mois',
      type: 'monthly',
      targetValue: 3,
      currentValue: 1,
      unit: 'courses',
      targetDate: '2024-02-29',
      isCompleted: false,
      createdAt: '2024-01-15',
      priority: 'medium'
    },
    {
      id: '3',
      title: 'Maîtriser JavaScript',
      description: 'Compléter tous les modules JavaScript avancés',
      type: 'skill_mastery',
      targetValue: 8,
      currentValue: 8,
      unit: 'modules',
      isCompleted: true,
      createdAt: '2023-12-01',
      priority: 'high'
    },
    {
      id: '4',
      title: 'Quiz hebdomadaires',
      description: 'Réussir au moins 5 quiz chaque semaine',
      type: 'weekly',
      targetValue: 5,
      currentValue: 3,
      unit: 'quizzes',
      isCompleted: false,
      createdAt: '2024-01-20',
      priority: 'low'
    }
  ]);

  const goalTypes = [
    { value: 'daily', label: 'Quotidien', icon: Calendar },
    { value: 'weekly', label: 'Hebdomadaire', icon: Calendar },
    { value: 'monthly', label: 'Mensuel', icon: Calendar },
    { value: 'course_completion', label: 'Complétion de cours', icon: BookOpen },
    { value: 'skill_mastery', label: 'Maîtrise de compétence', icon: Award }
  ];

  const units = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'lessons', label: 'Leçons' },
    { value: 'modules', label: 'Modules' },
    { value: 'quizzes', label: 'Quiz' },
    { value: 'courses', label: 'Cours' }
  ];

  const priorities = [
    { value: 'low', label: 'Basse', color: 'bg-gray-500' },
    { value: 'medium', label: 'Moyenne', color: 'bg-yellow-500' },
    { value: 'high', label: 'Haute', color: 'bg-red-500' }
  ];

  // Callback appelé quand le profil étudiant est chargé dans le layout
  const handleStudentProfileLoaded = (profile: any) => {
    setStudentProfile(profile);
    setIsLoading(false);
  };

  const handleCreateGoal = () => {
    if (!newGoal.title.trim()) {
      toast({
        title: 'Erreur',
        description: 'Le titre de l\'objectif est requis',
        variant: 'destructive'
      });
      return;
    }

    const goal: Goal = {
      id: Date.now().toString(),
      title: newGoal.title,
      description: newGoal.description,
      type: newGoal.type,
      targetValue: newGoal.targetValue,
      currentValue: 0,
      unit: newGoal.unit,
      targetDate: newGoal.targetDate || undefined,
      isCompleted: false,
      createdAt: new Date().toISOString(),
      priority: newGoal.priority
    };

    setGoals([...goals, goal]);
    setNewGoal({
      title: '',
      description: '',
      type: 'weekly',
      targetValue: 1,
      unit: 'lessons',
      targetDate: '',
      priority: 'medium'
    });
    setIsDialogOpen(false);
    
    toast({
      title: 'Succès',
      description: 'Objectif créé avec succès !'
    });
  };

  const handleDeleteGoal = (goalId: string) => {
    setGoals(goals.filter(g => g.id !== goalId));
    toast({
      title: 'Succès',
      description: 'Objectif supprimé'
    });
  };

  const handleToggleComplete = (goalId: string) => {
    setGoals(goals.map(g => 
      g.id === goalId 
        ? { ...g, isCompleted: !g.isCompleted, currentValue: g.isCompleted ? 0 : g.targetValue }
        : g
    ));
  };

  const filteredGoals = selectedFilter === 'all' 
    ? goals 
    : selectedFilter === 'completed'
    ? goals.filter(g => g.isCompleted)
    : goals.filter(g => !g.isCompleted);

  const completedCount = goals.filter(g => g.isCompleted).length;
  const activeCount = goals.filter(g => !g.isCompleted).length;
  const completionRate = goals.length > 0 ? Math.round((completedCount / goals.length) * 100) : 0;

  const getPriorityIcon = (priority: Goal['priority']) => {
    switch (priority) {
      case 'high': return <Flag className="h-4 w-4 text-red-500" />;
      case 'medium': return <Flag className="h-4 w-4 text-yellow-500" />;
      case 'low': return <Flag className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTypeIcon = (type: Goal['type']) => {
    const typeConfig = goalTypes.find(t => t.value === type);
    const Icon = typeConfig?.icon || Target;
    return <Icon className="h-4 w-4" />;
  };

  const headerContent = (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button className="flex items-center space-x-2">
          <Plus className="h-4 w-4" />
          <span>Nouvel objectif</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Créer un nouvel objectif</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Titre *
            </label>
            <Input
              value={newGoal.title}
              onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
              placeholder="Ex: Étudier quotidiennement"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Description
            </label>
            <Textarea
              value={newGoal.description}
              onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
              placeholder="Décrivez votre objectif..."
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Type
              </label>
              <Select value={newGoal.type} onValueChange={(value: Goal['type']) => setNewGoal({ ...newGoal, type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {goalTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Priorité
              </label>
              <Select value={newGoal.priority} onValueChange={(value: Goal['priority']) => setNewGoal({ ...newGoal, priority: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorities.map((priority) => (
                    <SelectItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Objectif
              </label>
              <Input
                type="number"
                value={newGoal.targetValue}
                onChange={(e) => setNewGoal({ ...newGoal, targetValue: parseInt(e.target.value) || 1 })}
                min={1}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Unité
              </label>
              <Select value={newGoal.unit} onValueChange={(value: Goal['unit']) => setNewGoal({ ...newGoal, unit: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {(newGoal.type === 'monthly' || newGoal.type === 'course_completion') && (
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Date limite
              </label>
              <Input
                type="date"
                value={newGoal.targetDate}
                onChange={(e) => setNewGoal({ ...newGoal, targetDate: e.target.value })}
              />
            </div>
          )}
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateGoal}>
              Créer l'objectif
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <StudentLayout
      title="Mes Objectifs"
      description="Définissez et suivez vos objectifs d'apprentissage personnalisés"
      onStudentProfileLoaded={handleStudentProfileLoaded}
      headerContent={headerContent}
    >
      <SEOMetaTags 
        title="Mes Objectifs - E-Learning"
        description="Définissez et suivez vos objectifs d'apprentissage personnalisés."
        keywords="objectifs, apprentissage, suivi, progression, e-learning"
      />

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{goals.length}</p>
              </div>
              <Target className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Actifs</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{activeCount}</p>
              </div>
              <Zap className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Terminés</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{completedCount}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Taux de réussite</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{completionRate}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtres */}
      <div className="flex space-x-2 mb-8">
        <Button
          variant={selectedFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('all')}
        >
          Tous ({goals.length})
        </Button>
        <Button
          variant={selectedFilter === 'active' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('active')}
        >
          Actifs ({activeCount})
        </Button>
        <Button
          variant={selectedFilter === 'completed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('completed')}
        >
          Terminés ({completedCount})
        </Button>
      </div>

      {/* Liste des objectifs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredGoals.map((goal, index) => {
          const progress = goal.targetValue > 0 ? (goal.currentValue / goal.targetValue) * 100 : 0;
          
          return (
            <motion.div
              key={goal.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`border-0 shadow-lg transition-all duration-300 hover:shadow-xl ${
                goal.isCompleted ? 'bg-green-50 dark:bg-green-900/20' : ''
              }`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {getTypeIcon(goal.type)}
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                          {goal.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {goalTypes.find(t => t.value === goal.type)?.label}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {getPriorityIcon(goal.priority)}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteGoal(goal.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  
                  {goal.description && (
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      {goal.description}
                    </p>
                  )}
                  
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Progression
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {goal.currentValue}/{goal.targetValue} {goal.unit}
                      </span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      {goal.targetDate && (
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-4 w-4" />
                          <span>{new Date(goal.targetDate).toLocaleDateString('fr-FR')}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>Créé le {new Date(goal.createdAt).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                    
                    <Button
                      size="sm"
                      variant={goal.isCompleted ? 'outline' : 'default'}
                      onClick={() => handleToggleComplete(goal.id)}
                      className="flex items-center space-x-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      <span>{goal.isCompleted ? 'Rouvrir' : 'Marquer terminé'}</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Message si aucun objectif */}
      {filteredGoals.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {selectedFilter === 'all' ? 'Aucun objectif défini' : `Aucun objectif ${selectedFilter === 'completed' ? 'terminé' : 'actif'}`}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {selectedFilter === 'all' 
              ? 'Commencez par définir vos premiers objectifs d\'apprentissage !'
              : 'Changez de filtre pour voir d\'autres objectifs.'
            }
          </p>
          {selectedFilter === 'all' && (
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Créer mon premier objectif
            </Button>
          )}
        </motion.div>
      )}
    </StudentLayout>
  );
};

export default StudentGoalsPage;
